import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getSubjectBounds,
  loadImageData,
  maskHasTransparentPixels,
} from "../../../utils/getBoundingBox";
import { subjectImageFrameStyle } from "../../../components/objectTransform/objectTransformMath";
import { markToolPrepSubjectAdjusted } from "../lib/toolPrepSubjectPlacement";

/**
 * CSS for the <img> inside a cropped subject wrapper — same math as CanvasEditor.
 */
export function subjectCroppedImageStyle(contentCrop) {
  if (!contentCrop?.width || !contentCrop?.height) {
    return {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      objectFit: "fill",
      maxWidth: "none",
      maxHeight: "none",
    };
  }
  const { x, y, width, height, imageWidth, imageHeight } = contentCrop;
  return {
    position: "absolute",
    ...subjectImageFrameStyle(
      { x, y, width, height },
      { width: imageWidth, height: imageHeight }
    ),
  };
}

function toSeed(fit) {
  if (!fit || !(fit.width > 0) || !(fit.height > 0)) return null;
  return {
    x: Number(fit.x ?? fit.left) || 0,
    y: Number(fit.y ?? fit.top) || 0,
    width: Number(fit.width) || 0,
    height: Number(fit.height) || 0,
    rotation: Number(fit.rotation) || 0,
  };
}

/**
 * Editor-like subject object for tool-prep:
 * - Fits full cutout on stage
 * - Tightens object box + contentCrop to opaque subject (getSubjectBounds)
 * - Interactive move / corner-scale / edge-crop / rotate (shared objectTransform)
 */
export function useToolPrepSubjectObject({
  enabled = false,
  imageUrl = null,
  /** Full-image fit on stage: { x|left, y|top, width, height } */
  fitFrame = null,
  resetKey = null,
}) {
  const seed = useMemo(() => toSeed(fitFrame), [
    fitFrame?.x,
    fitFrame?.y,
    fitFrame?.left,
    fitFrame?.top,
    fitFrame?.width,
    fitFrame?.height,
    fitFrame?.rotation,
  ]);

  const seedKey = useMemo(() => {
    if (!seed) return null;
    return [
      resetKey ?? "",
      Math.round(seed.x),
      Math.round(seed.y),
      Math.round(seed.width),
      Math.round(seed.height),
    ].join(":");
  }, [resetKey, seed]);

  const [object, setObjectState] = useState(null);
  const [contentCrop, setContentCrop] = useState(null);
  const [cropExtent, setCropExtent] = useState(null);
  const lastSeedKeyRef = useRef(null);
  const fullFitRef = useRef(null);

  useEffect(() => {
    if (!enabled || !seed || !seedKey) {
      setObjectState(null);
      setContentCrop(null);
      setCropExtent(null);
      lastSeedKeyRef.current = null;
      fullFitRef.current = null;
      return undefined;
    }

    if (lastSeedKeyRef.current === seedKey) {
      return undefined;
    }
    lastSeedKeyRef.current = seedKey;
    fullFitRef.current = seed;
    markToolPrepSubjectAdjusted(false);

    setObjectState({ ...seed });
    setContentCrop(null);
    setCropExtent(null);

    if (!imageUrl) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      let mask;
      try {
        mask = await loadImageData(imageUrl);
      } catch {
        return;
      }
      if (cancelled || !mask?.width || !mask?.height) return;
      if (!maskHasTransparentPixels(mask, 16)) return;

      const bounds = getSubjectBounds(mask, 0.9);
      if (!bounds || bounds.width <= 0 || bounds.height <= 0) return;

      const coverage =
        (bounds.width * bounds.height) / (mask.width * mask.height);
      if (coverage >= 0.97) return;

      const fit = fullFitRef.current;
      if (!fit) return;

      const scaleX = fit.width / mask.width;
      const scaleY = fit.height / mask.height;

      const subjectCrop = {
        x: bounds.left,
        y: bounds.top,
        width: bounds.width,
        height: bounds.height,
      };
      // Fixed extent so edge-crop can shrink/expand within subject only (Editor).
      setCropExtent({ ...subjectCrop });
      setContentCrop({
        ...subjectCrop,
        imageWidth: mask.width,
        imageHeight: mask.height,
      });
      setObjectState({
        x: fit.x + bounds.left * scaleX,
        y: fit.y + bounds.top * scaleY,
        width: Math.max(24, bounds.width * scaleX),
        height: Math.max(24, bounds.height * scaleY),
        rotation: fit.rotation || 0,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, seed, seedKey, imageUrl]);

  const setObject = useCallback((next) => {
    if (!next || !(next.width > 0) || !(next.height > 0)) return;
    markToolPrepSubjectAdjusted(true);
    setObjectState((prev) => ({
      x: next.x ?? next.left ?? 0,
      y: next.y ?? next.top ?? 0,
      width: next.width,
      height: next.height,
      rotation:
        next.rotation != null
          ? next.rotation
          : prev?.rotation || 0,
    }));
    if (next.crop && next.crop.width > 0 && next.crop.height > 0) {
      setContentCrop((prev) => ({
        ...(prev || {}),
        x: next.crop.x,
        y: next.crop.y,
        width: next.crop.width,
        height: next.crop.height,
        imageWidth: prev?.imageWidth || next.crop.imageWidth,
        imageHeight: prev?.imageHeight || next.crop.imageHeight,
      }));
    }
  }, []);

  const wrapperStyle = useMemo(() => {
    if (!object) return undefined;
    return {
      position: "absolute",
      left: object.x,
      top: object.y,
      width: object.width,
      height: object.height,
      overflow: "hidden",
      boxSizing: "border-box",
      transform:
        object.rotation && object.rotation !== 0
          ? `rotate(${object.rotation}deg)`
          : undefined,
      transformOrigin: "center center",
    };
  }, [object]);

  const imageStyle = useMemo(
    () => subjectCroppedImageStyle(contentCrop),
    [contentCrop]
  );

  const originalImageSize = useMemo(() => {
    if (!contentCrop?.imageWidth) return null;
    return {
      width: contentCrop.imageWidth,
      height: contentCrop.imageHeight,
    };
  }, [contentCrop?.imageWidth, contentCrop?.imageHeight]);

  const cropForTransform = useMemo(() => {
    if (!contentCrop?.width) return null;
    return {
      x: contentCrop.x,
      y: contentCrop.y,
      width: contentCrop.width,
      height: contentCrop.height,
    };
  }, [contentCrop]);

  return {
    object: enabled ? object : null,
    setObject,
    contentCrop: enabled ? contentCrop : null,
    crop: enabled ? cropForTransform : null,
    cropExtent: enabled ? cropExtent : null,
    originalImageSize: enabled ? originalImageSize : null,
    wrapperStyle: enabled ? wrapperStyle : undefined,
    imageStyle: enabled ? imageStyle : undefined,
    tightened: Boolean(contentCrop),
  };
}
